/**
 * WenkTagLogo - Custom Adaptive Logo Component
 * 
 * A modern, professional logo for WenkTag GPS that:
 * - Adapts to light/dark backgrounds
 * - Uses the brand's soft teal accent color
 * - Is scalable SVG for crisp display
 */

import React from 'react';
import Box from '@mui/material/Box';
import { useSelector } from 'react-redux';

const WenkTagLogo = ({
    height = 40,
    variant = 'light', // 'light' for light backgrounds, 'dark' for dark backgrounds
    showText = true,
    usePng = false // Reverting to SVG as default for better transparency and scaling
}) => {
    // Color scheme based on variant
    const colors = variant === 'light' ? {
        primary: '#4ECDC4',      // Soft teal
        secondary: '#6C9BCF',   // Soft blue
        text: '#1E293B',        // Dark text 
        accent: '#10B981',      // Green accent
    } : {
        primary: '#4ECDC4',
        secondary: '#6C9BCF',
        text: '#FFFFFF',
        accent: '#10B981',
    };

    const logoHeight = height;
    const direction = useSelector((state) => state.settingsData.directions)

    if (usePng) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: height,
                    direction: 'ltr',
                }}
            >
                <img
                    src={showText ? "/wenktag_logo.png" : "/wenktag_favicon.png"}
                    alt="WenkTag Logo"
                    style={{
                        height: '100%',
                        width: 'auto',
                        display: 'block'
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                height: logoHeight,
                direction: 'ltr',
            }}
        >
            {/* Text Part - Moved to the left */}
            {showText && (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px', direction: `${direction} !important` }}>
                        <span style={{
                            fontSize: logoHeight * 0.52,
                            fontWeight: 850,
                            fontFamily: 'Cairo, Inter, sans-serif',
                            color: colors.text,
                            letterSpacing: '-1px',
                            lineHeight: 1,
                        }}>
                            Wenk
                        </span>
                        <span style={{
                            fontSize: logoHeight * 0.52,
                            fontWeight: 850,
                            fontFamily: 'Cairo, Inter, sans-serif',
                            color: colors.primary,
                            letterSpacing: '-1px',
                            lineHeight: 1,
                        }}>
                            Tag
                        </span>
                    </Box>
                    <span style={{
                        fontSize: logoHeight * 0.2,
                        fontWeight: 700,
                        fontFamily: 'Cairo, Inter, sans-serif',
                        color: colors.secondary,
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                        marginTop: '3px',
                        opacity: 0.9
                    }}>
                        GPS Tracking
                    </span>
                </Box>
            )}

            {/* Icon Part - Moved to the right */}
            <svg
                width={logoHeight * 1.1}
                height={logoHeight * 1.1}
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.05))' }}
            >
                <defs>
                    <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={colors.primary} />
                        <stop offset="100%" stopColor={colors.secondary} />
                    </linearGradient>
                </defs>

                {/* Outer concentric rings */}
                <circle cx="24" cy="24" r="21" stroke={colors.primary} strokeWidth="1.5" fill="none" opacity="0.15" />
                <circle cx="24" cy="24" r="17" stroke={colors.primary} strokeWidth="1.5" fill="none" opacity="0.3" />

                {/* Location Pin */}
                <path
                    d="M24 10C18.48 10 14 14.48 14 20C14 27.5 24 38 24 38C24 38 34 27.5 34 20C34 14.48 29.52 10 24 10Z"
                    fill="url(#logoGradient)"
                />

                {/* Pin center dot */}
                <circle cx="24" cy="20" r="4.5" fill="#FFFFFF" />

                {/* Satellite dots */}
                <circle cx="39" cy="14" r="2.5" fill={colors.primary} />
                <circle cx="43" cy="21" r="1.8" fill={colors.secondary} />
                <circle cx="10" cy="17" r="2" fill={colors.primary} opacity="0.6" />
            </svg>
        </Box>
    );
};

export default WenkTagLogo;
